// Web push on this phone (FE-51, docs/web-push.md): whether it can be offered at all, the ask after
// a first finished round, this browser's subscription and its kinds, and the small reports the
// server counts (the ask's funnel, leaving the tab while seated, for Park ready).
//
// Only outside any frame (the Lagos Life frame can't ask for permission, and its players give us
// nothing), only for our own accounts, only where the server offers a key, and only in a browser
// with a service worker and push. iPhone and iPad allow push only to a game added to the home screen
// (iOS 16.4 and later), so there the bar never shows and Settings says how. We never ask during a
// round or on a first visit; Not now waits 14 days; a browser refusal is final (only the browser's
// own settings undo it).
import { isFramed } from '@wahala/vatar-client';
import { useUi } from '../store/ui';
import { roundsDone } from './account';
import { api, loadSession, type PushKind, type PushStep } from './api';

export const PUSH_KINDS: PushKind[] = ['calls', 'park', 'today', 'rankings', 'news'];
/** Not now: the bar comes back after this long at the earliest. */
export const ASK_AGAIN_MS = 14 * 86_400_000;
const LATER = 'wahala.push.later';

export type PushStatus =
  /** Not here: framed, a distributor's player, no key from the server, or no push in this browser. */
  | 'none'
  /** An iPhone or iPad not opened from the home screen. */
  | 'install'
  /** The browser was never asked. */
  | 'ask'
  /** The browser said no; only its own settings change that. */
  | 'blocked'
  /** Allowed, but this browser has no subscription with us (turned off, or it ran out). */
  | 'off'
  | 'on';

export interface PushView {
  status: PushStatus;
  kinds: PushKind[];
  busy: boolean;
}

let view: PushView = { status: 'none', kinds: [], busy: false };
const listeners = new Set<(v: PushView) => void>();
function set(patch: Partial<PushView>): void {
  view = { ...view, ...patch };
  if (view.status === 'on' && patch.kinds) keepKinds(view.kinds);
  for (const f of listeners) f(view);
}
export const pushView = () => view;
export function onPush(f: (v: PushView) => void): () => void {
  listeners.add(f);
  return () => listeners.delete(f);
}

/** Opened from the home screen (an installed app). */
export function installed(): boolean {
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return true;
  } catch {
    // no matchMedia
  }
  return Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

/** An iPhone or iPad (iPadOS says it is a Mac with a touch screen), in any browser. */
export function isIos(nav: Pick<Navigator, 'userAgent' | 'maxTouchPoints'> = navigator): boolean {
  return /iP(hone|od|ad)/.test(nav.userAgent) || (/Macintosh/.test(nav.userAgent) && nav.maxTouchPoints > 1);
}

/** Could this phone get pushes from us at all (whatever the browser's answer so far)? */
export function pushOffered(): boolean {
  const u = useUi.getState();
  if (isFramed() || !u.config?.push?.publicKey || u.account === 'platform' || !u.me) return false;
  return (
    typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
  );
}

/** The service worker, if it is running (it registers after load, in built clients). */
async function registration(): Promise<ServiceWorkerRegistration | null> {
  try {
    const r = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 4_000)),
    ]);
    return r ?? null;
  } catch {
    return null;
  }
}

const keyBytes = (b64: string) => {
  const raw = atob(b64.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};
const b64url = (buf: ArrayBuffer | null) =>
  buf
    ? btoa(String.fromCharCode(...new Uint8Array(buf)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '')
    : '';

async function current(): Promise<PushSubscription | null> {
  const r = await registration();
  try {
    return (await r?.pushManager.getSubscription()) ?? null;
  } catch {
    return null;
  }
}

/**
 * Read where this phone stands, and check an existing subscription in with the server (so it is
 * kept, its install state is fresh, and a phone whose subscription the browser renewed is known).
 */
export async function refreshPush(): Promise<PushView> {
  if (!pushOffered()) {
    set({ status: 'none', kinds: [] });
    return view;
  }
  if (isIos() && !installed()) {
    set({ status: 'install', kinds: [] });
    return view;
  }
  const perm = Notification.permission;
  if (perm === 'denied') set({ status: 'blocked', kinds: [] });
  else if (perm === 'default') set({ status: 'ask', kinds: [] });
  else {
    const sub = await current();
    if (!sub) set({ status: 'off', kinds: [] });
    else {
      try {
        const s = await api.pushSubscribe(body(sub));
        set({ status: 'on', kinds: s.kinds });
      } catch {
        set({ status: 'off', kinds: [] });
      }
    }
  }
  return view;
}

const KINDS_KEPT = 'wahala.push.kinds';
/** The kinds as the server last said, kept on the phone for a subscription the browser renews. */
function keepKinds(kinds: PushKind[]): void {
  try {
    localStorage.setItem(KINDS_KEPT, kinds.join(','));
  } catch {
    // storage blocked: a renewed subscription starts from the defaults
  }
}
function keptKinds(): PushKind[] | undefined {
  try {
    const raw = localStorage.getItem(KINDS_KEPT);
    return raw === null ? undefined : PUSH_KINDS.filter((k) => raw.split(',').includes(k));
  } catch {
    return undefined;
  }
}

const body = (sub: PushSubscription) => {
  const fallback = keptKinds();
  return {
    endpoint: sub.endpoint,
    keys: { p256dh: b64url(sub.getKey('p256dh')), auth: b64url(sub.getKey('auth')) },
    installed: installed(),
    ...(fallback ? { fallback } : {}),
  };
};

/**
 * Turn notifications on: the browser's own prompt if it was never asked (only ever after the
 * player said Yes in our bar or tapped Turn on), then a subscription for our key, saved with us.
 */
export async function turnOn(): Promise<PushStatus> {
  const key = useUi.getState().config?.push?.publicKey;
  if (!key || !pushOffered()) return 'none';
  set({ busy: true });
  try {
    if (Notification.permission === 'default') {
      const answer = await Notification.requestPermission();
      report(answer === 'granted' ? 'granted' : 'denied');
      if (answer !== 'granted') {
        set({ status: answer === 'denied' ? 'blocked' : 'ask' });
        return view.status;
      }
    }
    if (Notification.permission !== 'granted') {
      set({ status: 'blocked' });
      return 'blocked';
    }
    const r = await registration();
    if (!r) throw new Error('no service worker');
    const sub =
      (await r.pushManager.getSubscription()) ??
      (await r.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
    const s = await api.pushSubscribe(body(sub));
    set({ status: 'on', kinds: s.kinds });
    return 'on';
  } catch {
    set({ status: Notification.permission === 'granted' ? 'off' : view.status });
    throw new Error('push_failed');
  } finally {
    set({ busy: false });
  }
}

/** This browser stops: the browser's subscription and ours go (the permission stays as it is). */
export async function turnOff(): Promise<void> {
  const sub = await current();
  set({ busy: true });
  try {
    if (sub) {
      await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
      await sub.unsubscribe().catch(() => false);
    }
    set({ status: Notification.permission === 'granted' ? 'off' : view.status, kinds: [] });
  } finally {
    set({ busy: false });
  }
}

/** One kind on or off for this browser. */
export async function setKind(kind: PushKind, on: boolean): Promise<void> {
  const sub = await current();
  if (!sub) return;
  const want = PUSH_KINDS.filter((k) => (k === kind ? on : view.kinds.includes(k)));
  const before = view.kinds;
  set({ kinds: want });
  try {
    set({ kinds: (await api.pushKinds(sub.endpoint, want)).kinds });
  } catch {
    set({ kinds: before });
    throw new Error('push_failed');
  }
}

/** "Send me a test", to this browser. */
export async function sendTest(): Promise<void> {
  const sub = await current();
  await api.pushTest(sub?.endpoint);
}

/** Signing out on this phone: its notifications go with the session. */
export async function forgetPush(): Promise<void> {
  if (view.status !== 'on') return;
  await turnOff().catch(() => undefined);
}

// ---------------------------------------------------------------- the ask

function laterAt(): number {
  try {
    return Number(localStorage.getItem(LATER) ?? 0) || 0;
  } catch {
    return 0;
  }
}

/** May the bar ask now? After a finished round, never twice inside 14 days, never after a refusal. */
export function mayAsk(now = Date.now()): boolean {
  return view.status === 'ask' && roundsDone() >= 1 && now - laterAt() >= ASK_AGAIN_MS;
}

/** Not now: the bar waits 14 days. */
export function askLater(now = Date.now()): void {
  try {
    localStorage.setItem(LATER, String(now));
  } catch {
    // storage blocked: the bar may come back on the next visit
  }
  report('later');
}

/** A step of the ask, for the office's funnel: a count, nothing about the player. */
export function report(step: PushStep): void {
  if (!loadSession()) return;
  void api.pushFunnel(step).catch(() => undefined);
}

// ---------------------------------------------------------------- Park ready

/**
 * Seated in a park's lobby and the tab goes out of sight (or comes back): the server hears it, so
 * if the park fills meanwhile this phone gets Park ready. keepalive, so it goes as the tab hides.
 */
let awayIn: string | null = null;
export function reportAway(code: string, away: boolean): void {
  // only "back" for a park we said we left
  if (!away && awayIn !== code) return;
  if (away && (view.status !== 'on' || !view.kinds.includes('park'))) return;
  awayIn = away ? code : null;
  const s = loadSession();
  if (!s) return;
  try {
    void fetch('/api/v1/push/away', {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/json', authorization: `Bearer ${s.token}`, 'x-wahala-mode': s.mode },
      body: JSON.stringify({ code, away }),
    }).catch(() => undefined);
  } catch {
    // nothing to do: Park ready falls back on the worker's word
  }
}

// ---------------------------------------------------------------- where a tap opens the game

export type Opening = { screen: 'today' | 'board' | 'shop' | 'pass' | 'people' } | null;

/** The screens ?open= may name: a notification's, and the website's /play/{screen} links. */
const OPENS = new Set(['today', 'board', 'shop', 'pass']);
/** An opening read before the player had a name: the name screen takes it (heldOpening). */
let held: Opening = null;

/**
 * A tap on a notification, or a wahala.io/play/{screen} link (docs/wahala-io-urls.md), opens the
 * game with what to show in the address: ?open=today|board|shop|pass, or ?call={party} (a crew
 * call: the people screen holds it). Read once at boot and taken out of the address, like an
 * invite's ?park=. A new player picks a name first and lands there after (heldOpening).
 */
export function takeOpening(): Opening {
  const q = new URLSearchParams(location.search);
  if (!q.has('open') && !q.has('call')) return null;
  const open = q.get('open');
  const call = q.has('call');
  q.delete('open');
  q.delete('call');
  const rest = q.toString();
  history.replaceState(history.state, '', `${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`);
  held = call
    ? { screen: 'people' }
    : open && OPENS.has(open)
      ? { screen: open as 'today' | 'board' | 'shop' | 'pass' }
      : null;
  return held;
}

/** The opening a new player came in with, once: the name screen goes there instead of the menu. */
export function heldOpening(): Opening {
  const o = held;
  held = null;
  return o;
}
